import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import Typography from 'kingpin/atoms/Typography';
import Loading from 'components/Loading/Loading';
import brandingLightPng from 'kingpin/atoms/Icon/assets/Branding/large-light.png';
import useDarkModeToggle from 'theme/useDarkModeToggle';

import { ReactComponent as BrandLogo } from '../../images/brand-dark.svg';

/**
 * The single full-screen loader shown during cold boot — Firebase init, the
 * grouped loading overlay, and the splash screen all render this so startup
 * reads as one calm, branded wait rather than a series of different spinners.
 *
 * After a threshold we surface a gentle "taking longer than expected" note so a
 * slow network or stalled load feels intentional instead of broken.
 *
 * The logo is imported as an inline SVG component (SVGR `ReactComponent`), not
 * fetched-and-injected via react-svg. Cold boot mounts this loader several times
 * in a row as each gate (Firebase → auth → grouped → splash) hands off to the
 * next; an async-fetched logo blanks then re-appears on every mount, which reads
 * as the loader "remounting/flickering". An inline SVG renders synchronously and
 * identically each time, so the hand-offs look like one continuous loader.
 *
 * In dark mode the SVG's dark wordmark is unreadable, so we use the header's
 * light PNG instead. That one is fetched, but after the first mount it comes
 * from the browser cache, so the hand-offs still don't flicker.
 */
const SLOW_LOAD_MS = 12000;

// Cold boot mounts this loader several times in quick succession as each gate
// hands off to the next, so a per-mount timer would restart the "taking longer
// than expected" countdown on every hand-off and never actually fire during a
// slow multi-stage boot. Instead we measure from the start of the current
// loading *episode*, shared across those rapid remounts via module scope. A
// long gap since the last instance was mounted (e.g. a reload well after the
// app was up) begins a fresh episode. `EPISODE_GAP_MS` must comfortably exceed
// the near-instant gate hand-off gap while staying below a real reload gap.
const EPISODE_GAP_MS = 3000;
let episodeStartedAt = Date.now();
let lastMountedAt = Date.now();

const Root = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const Logo = styled.div`
  opacity: 0.95;

  svg,
  img {
    width: 150px;
    height: auto;
    display: block;
  }
`;

/**
 * The base Loading's own wrapper uses flex: 1, so it must not sit directly in
 * Root's flex column or it would stretch and shove the logo to the top. This
 * content-sized wrapper contains that growth so the stack stays centred.
 */
const SpinnerWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SlowMessage = styled.div`
  max-width: 320px;
  text-align: center;
`;

const BrandedBootLoader = ({ testId }: { testId?: string }) => {
  const [isSlow, setIsSlow] = useState(false);
  const { isDarkMode } = useDarkModeToggle();

  useEffect(() => {
    const now = Date.now();
    // A long gap since a boot loader was last mounted means this is a new
    // loading episode (not a gate hand-off), so restart the slow-load clock.
    if (now - lastMountedAt > EPISODE_GAP_MS) {
      episodeStartedAt = now;
    }

    // Capped: if the clock has gone back since the episode started (or a
    // test has moved it), the wait would otherwise run to years, overflow
    // setTimeout and fire at once.
    const remaining = Math.min(
      SLOW_LOAD_MS,
      SLOW_LOAD_MS - (now - episodeStartedAt),
    );
    if (remaining <= 0) {
      setIsSlow(true);
      return;
    }

    const timeout = setTimeout(() => setIsSlow(true), remaining);
    return () => {
      clearTimeout(timeout);
      // Stamp on unmount so the near-instant hand-off to the next gate's loader
      // is seen as the same episode rather than a fresh one.
      lastMountedAt = Date.now();
    };
  }, []);

  return (
    <Root data-testid={testId}>
      <Logo>
        {isDarkMode ? <img src={brandingLightPng} alt="" /> : <BrandLogo />}
      </Logo>
      <SpinnerWrap>
        <Loading isGrouped />
      </SpinnerWrap>
      {isSlow && (
        <SlowMessage>
          <Typography.Body type="Body 12">
            This is taking longer than expected. Check your connection or
            refresh the page.
          </Typography.Body>
        </SlowMessage>
      )}
    </Root>
  );
};

export default BrandedBootLoader;
